import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import data from "../content/case-studies.json";
import stats from "./content/site-stats.json";
import { DRAFT_SERVICES } from "./content/draft-services";
import { CTABand, PrimaryButton } from "../components/Buttons";
import { WinsTeaser } from "./WinsTeaser";
import { CaseChart } from "../components/CaseChart";
import { SnapshotTable } from "../components/SnapshotTable";
import { Gallery } from "../components/Gallery";
import { EmailShowcase } from "../components/EmailShowcase";
import { useMeta } from "../hooks/useMeta";
import { draftPath } from "./DraftLayout";
import { Shell, H2, Reveal, StatRow, Steps, caseBySlug } from "./parts";

const NAME_TO_KEY = { "Paid Traffic": "paid-traffic", "Ad Creative": "ad-creative", Retention: "retention", AEO: "aeo" };

function HeroVisual({ s }) {
  if (s.heroVisual === "gallery") {
    const imgs = ["/assets/jamie-anne/ja-oil-review.webp", "/assets/good-riddance/gr27-poster.jpg", "/assets/clean-eez/grout-eez.webp", "/assets/jamie-anne/ja-roller-ugc.webp"];
    return (
      <div className="grid grid-cols-2 gap-3">
        {imgs.map((src, i) => <img key={src} src={src} alt="" loading="lazy" className={`w-full rounded-xl object-cover shadow-lg ${i % 2 ? "mt-6" : ""}`} style={{ aspectRatio: "4 / 5" }} />)}
      </div>
    );
  }
  if (s.heroVisual === "emails") {
    const emails = ["/assets/jamie-anne/steak.webp", "/assets/smart-earth/barn-program.webp", "/assets/jamie-anne/bare-minimum.webp"];
    return (
      <div className="grid grid-cols-3 gap-3">
        {emails.map((src, i) => (
          <div key={src} className={`h-[380px] overflow-hidden rounded-2xl border-4 border-white/20 bg-white shadow-lg ${i === 1 ? "-mt-4" : "mt-4"}`}>
            <img src={src} alt="" loading="lazy" className="w-full" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/15 bg-white/5 p-6 text-sm leading-relaxed text-white/80">
      <div className="text-xs font-bold uppercase tracking-widest text-white/50">ChatGPT</div>
      <p className="mt-3 font-medium text-white">Where can I buy the best lightweight backpacking gear?</p>
      <p className="mt-4">For ultralight tents and sleeping bags sold direct, <span className="rounded bg-pink/20 px-1 font-bold text-white">Outdoor Vitals</span> is a strong option: founder-led, premium materials, and consistently well reviewed by thru-hikers. Also worth comparing: Zpacks, Enlightened Equipment and Gossamer Gear.</p>
      <div className="mt-4 text-xs text-white/50">Illustrative. Based on a tracked query, May 2026.</div>
    </div>
  );
}

function PrincipleTile({ v }) {
  if (v.type === "image") {
    return (
      <div data-testid="principle-tile-image" className="relative h-48 overflow-hidden bg-fog">
        <img src={v.src} alt={v.caption || ""} loading="lazy" className="absolute inset-x-0 top-0 mx-auto w-[70%] rounded-t-lg border border-mist shadow-md" style={{ objectFit: "cover", objectPosition: "top" }} />
        {v.caption && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/90 to-transparent px-5 pb-3 pt-8 text-xs font-medium text-navy/70">{v.caption}</div>}
      </div>
    );
  }
  if (v.type === "stat") {
    return (
      <div data-testid="principle-tile-stat" className="flex h-48 flex-col justify-center bg-navy px-7">
        <div className="text-5xl font-extrabold leading-none text-white">{v.value}</div>
        <div className="mt-3 text-xs font-medium uppercase tracking-widest text-white/60">{v.label}</div>
        {v.client && <div className="mt-2 text-xs text-pink">{v.client}</div>}
      </div>
    );
  }
  return (
    <div data-testid="principle-tile-rows" className="h-48 bg-fog px-7 py-5">
      {v.client && <div className="text-[10px] font-bold uppercase tracking-widest text-navy/50">{v.client}</div>}
      <table className="mt-2 w-full text-sm">
        <tbody>
          {v.rows.map((r) => (
            <tr key={r.metric} className="border-t border-mist first:border-0">
              <td className="py-2 pr-2 font-medium text-navy">{r.metric}</td>
              <td className="py-2 pr-2 text-navy/50">{r.before}</td>
              <td className="py-2 text-right font-bold text-green">{r.after}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmbeddedProof({ embed }) {
  const cs = caseBySlug(embed.slug);
  if (!cs) return null;
  const chart = embed.chartType ? cs.charts.find((c) => c.type === embed.chartType) : null;
  const snap = embed.snapshotIndex != null ? cs.snapshots[embed.snapshotIndex] : null;
  return (
    <Shell tone="fog" testId="service-proof-chart">
      {embed.title && <H2>{embed.title}</H2>}
      {embed.intro && <p className="mt-3 max-w-3xl text-base text-navy/70 md:text-lg">{embed.intro}</p>}
      <div className={`mt-10 grid gap-8 ${snap && chart ? "lg:grid-cols-[1.4fr_1fr]" : ""}`}>
        {chart && <div className="[&_table]:hidden"><CaseChart chart={chart} /></div>}
        {snap && <SnapshotTable snapshot={snap} testId="service-proof-snapshot" />}
      </div>
      <p className="mt-6 text-base font-medium text-navy">
        {embed.caption}{" "}
        <Link to={`${draftPath("/case-studies")}/${cs.slug}`} data-testid="service-proof-link" className="font-bold text-link hover:underline">Read the case study</Link>
      </p>
    </Shell>
  );
}

export default function ServicePage() {
  const { service } = useParams();
  const s = DRAFT_SERVICES[service];
  useMeta({ title: s ? `${s.name} | Smart Marketer Agency` : "Not found", description: s ? s.sub : "" });
  if (!s) {
    if (data.cases.some((c) => c.slug === service)) return <Navigate to={`/case-studies/${service}`} replace />;
    return (
      <main data-testid="draft-not-found" className="bg-white">
        <div className="mx-auto max-w-4xl px-5 py-24 text-center text-2xl font-bold text-navy">Page not found.</div>
      </main>
    );
  }

  const cases = data.cases.filter((c) => c.status === "published" && c.services.some((n) => NAME_TO_KEY[n] === s.key)).slice(0, 4);
  const emailCases = data.cases.filter((c) => c.designs && c.designs.length);
  const emails = emailCases.flatMap((c) => c.designs.map((d) => ({ ...d, caption: `${c.client}. ${d.caption}` })));

  return (
    <main data-testid={`draft-service-${s.key}`}>
      <section className="bg-navy">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 md:grid-cols-2 md:py-24">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">{s.name}</div>
            <h1 className="mt-4 text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[52px]">{s.h1}</h1>
            <p className="mt-6 text-base leading-relaxed text-white/75 md:text-lg">{s.sub}</p>
          </div>
          <Reveal><HeroVisual s={s} /></Reveal>
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-6xl px-5 py-12"><StatRow items={s.stats} dark testId="service-stats" /></div>
        </div>
      </section>

      <Shell testId="sound-familiar">
        <H2>Sound familiar?</H2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {s.problemQuotes.map((q) => (
            <blockquote key={q} className="rounded-2xl bg-fog p-7 ring-1 ring-navy/[0.04] text-xl font-bold text-navy">"{q}"</blockquote>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-base leading-relaxed text-navy/80 md:text-lg">...{s.problemLine}</p>
      </Shell>

      <Shell tone="fog" testId="how-we-run-it">
        <H2>{s.howTitle || "How we run it."}</H2>
        {s.howIntro && <p className="mt-3 max-w-3xl text-base text-navy/70 md:text-lg">{s.howIntro}</p>}
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {s.how.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.05} className="overflow-hidden rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)]">
              {h.visual && <PrincipleTile v={h.visual} />}
              <div className="p-7">
                {h.tag && <div data-testid="principle-tag" className="mb-2 text-[10px] font-extrabold uppercase tracking-widest text-pink">{h.tag}</div>}
                <h3 className="text-lg font-bold text-navy">{h.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-navy/80">{h.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Shell>

      {(s.extraSections || []).map((x) => {
        const dark = x.tone === "navy";
        return (
          <Shell key={x.title} tone={x.tone || "white"} testId={`service-extra-${x.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <H2 light={dark}>{x.title}</H2>
            {x.intro && <p className={`mt-3 max-w-3xl text-base md:text-lg ${dark ? "text-white/70" : "text-navy/70"}`}>{x.intro}</p>}
            {x.pullQuote && (
              <figure className={`mt-8 max-w-3xl border-l-4 border-pink pl-6 ${dark ? "text-white" : "text-navy"}`}>
                <blockquote className="text-xl font-bold leading-snug md:text-2xl">"{x.pullQuote.text}"</blockquote>
                <figcaption className={`mt-3 text-sm ${dark ? "text-white/60" : "text-navy/60"}`}><span className="font-bold">{x.pullQuote.name}</span>, {x.pullQuote.title}</figcaption>
              </figure>
            )}
            <div className={`mt-10 grid gap-6 ${x.items.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
              {x.items.map((it, i) => (
                <Reveal key={it.title} delay={i * 0.06} className={`rounded-xl p-7 ${dark ? "bg-white/5" : "border border-mist bg-white"}`}>
                  <h3 className={`text-lg font-bold ${dark ? "text-white" : "text-navy"}`}>{it.title}</h3>
                  <p className={`mt-3 text-base leading-relaxed ${dark ? "text-white/75" : "text-navy/80"}`}>{it.body}</p>
                </Reveal>
              ))}
            </div>
            {x.gallerySlug && (
              <div className={`mt-14 rounded-2xl p-6 md:p-10 ${dark ? "bg-white" : "bg-fog"}`}>
                <Gallery items={[x.gallerySlug, ...(x.moreGallerySlugs || [])].flatMap((g) => caseBySlug(g).gallery)} title={x.galleryTitle || "The creative"} />
              </div>
            )}
            {x.cta && <div className="mt-10"><PrimaryButton testId="service-extra-cta">{x.cta.label}</PrimaryButton></div>}
          </Shell>
        );
      })}

      {s.embedChart && <EmbeddedProof embed={s.embedChart} />}

      {s.callout && (
        <section data-testid="service-callout" className="bg-navy">
          <div className="mx-auto max-w-6xl px-5 py-12">
            <p className="text-lg font-medium leading-relaxed text-white md:text-xl">{s.callout.text}</p>
            <a href={stats.links[s.callout.linkKey]} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-link hover:underline">{s.callout.linkLabel} <ArrowRight size={16} /></a>
          </div>
        </section>
      )}

      {s.creativeNote && (
        <Shell testId="service-creative-note">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <H2>Personalized creative.</H2>
              <p className="mt-5 text-base leading-relaxed text-navy/80 md:text-lg">{s.creativeNote.text}</p>
              <Link to={draftPath(s.creativeNote.to)} className="mt-6 inline-flex items-center gap-2 font-bold text-link hover:underline">{s.creativeNote.linkLabel} <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["gr23", "gr33", "gr78"].map((g) => <img key={g} src={`/assets/good-riddance/${g}.webp`} alt="" loading="lazy" className="w-full rounded-xl" />)}
            </div>
          </div>
        </Shell>
      )}

      {s.gallerySlug && (
        <Shell tone="fog" testId="service-gallery"><Gallery items={caseBySlug(s.gallerySlug).gallery} /></Shell>
      )}

      {s.emailShowcase && emails.length > 0 && (
        <>
          <Shell testId="design-that-sells" className="!pb-0">
            <H2>Design that sells.</H2>
            <p className="mt-3 max-w-2xl text-base text-navy/70 md:text-lg">Every email is designed to the brand's standard.</p>
          </Shell>
          <EmailShowcase items={emails.slice(0, 6)} />
        </>
      )}

      {s.timeline && <Steps lead={s.timelineLead || "From access to takeover takes 10 business days."} steps={s.timeline} tone={s.extraSections ? "fog" : "white"} label={s.timelineLabel || "Step"} />}

      {s.pricing && stats.showPricing && (
        <Shell testId="pricing">
          <H2>Pricing.</H2>
          <p className="mt-4 text-lg text-navy">{s.pricing.line}</p>
          <ul className="mt-4 space-y-1 text-base text-navy/70">{s.pricing.examples.map((e) => <li key={e}>{e}</li>)}</ul>
        </Shell>
      )}

      <Shell tone={s.timeline ? "fog" : "white"} testId="service-cases">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <H2>Proof.</H2>
            <div className="mt-8 space-y-6">
              {stats.textTestimonials.slice(0, 2).map((t) => (
                <figure key={t.name} className="rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] p-7">
                  <blockquote className="text-base leading-relaxed text-navy">"{t.quote}"</blockquote>
                  <figcaption className="mt-4 text-xs text-navy/60"><span className="font-bold text-navy">{t.name}</span>, {t.title}</figcaption>
                </figure>
              ))}
              {s.statCards && s.statCards.map((c) => (
                <div key={c.value} className="rounded-xl bg-navy p-6">
                  <div className="text-3xl font-extrabold text-white">{c.value}</div>
                  <div className="mt-2 text-xs uppercase tracking-widest text-white/60">{c.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-5">
            {cases.length > 0 ? <WinsTeaser cases={cases} /> : <p className="text-navy/60">Case studies for {s.name} are being written.</p>}
            {cases.length > 0 && (
              <Link to={draftPath("/case-studies")} data-testid="service-all-cases" className="inline-flex items-center gap-2 text-sm font-bold text-link hover:underline">All case studies <ArrowRight size={16} /></Link>
            )}
          </div>
        </div>
      </Shell>

      <CTABand title="See if we're a fit." sub={`Work with our Agency to get ${s.name.toLowerCase()} tailored to your needs and executed by our results-driven team.`} />
    </main>
  );
}
